import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Camera, ChevronLeft, ChevronRight, ImagePlus, Loader2, Sparkles, Trash2, User } from "lucide-react";
import { toast } from "sonner";

import api, { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const STEPS = [
  { key: "profile", title: "Tell us about you", icon: User },
  { key: "organization", title: "Your organization", icon: Building2 },
  { key: "branding", title: "Add your branding", icon: ImagePlus },
];

const MAX_BYTES = 2 * 1024 * 1024;

function readFileAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function ImageDrop({ value, onChange, label, hint, testid, rounded = false }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const handle = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error("Image must be under 2 MB");
      return;
    }
    setBusy(true);
    try {
      const dataUrl = await readFileAsDataURL(file);
      onChange(dataUrl);
    } catch (e) {
      toast.error("Couldn't read that file");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium text-slate-800">{label}</Label>
      <div
        className={`relative border-2 border-dashed border-slate-300 hover:border-emerald-400 transition-colors p-5 flex items-center gap-4 cursor-pointer bg-slate-50/60 ${rounded ? "rounded-full" : "rounded-md"}`}
        onClick={() => fileRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onDrop={(e) => {
          e.preventDefault();
          handle(e.dataTransfer.files?.[0]);
        }}
        data-testid={testid}
      >
        {value ? (
          <img
            src={value}
            alt={label}
            className={`object-cover bg-white ring-1 ring-slate-200 ${rounded ? "h-16 w-16 rounded-full" : "h-16 w-24 rounded-sm"}`}
          />
        ) : (
          <div className={`grid place-items-center bg-white ring-1 ring-slate-200 ${rounded ? "h-16 w-16 rounded-full" : "h-16 w-24 rounded-sm"}`}>
            {rounded ? <Camera className="w-6 h-6 text-slate-400" /> : <ImagePlus className="w-6 h-6 text-slate-400" />}
          </div>
        )}
        <div className="flex-1">
          <div className="text-sm font-medium text-slate-800">
            {value ? "Replace" : "Choose"} a file or drop it here
          </div>
          <div className="text-xs text-slate-500 mt-0.5">{hint || "PNG, JPG, or SVG · up to 2 MB"}</div>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => handle(e.target.files?.[0])}
          data-testid={`${testid}-input`}
        />
        {value && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-rose-600 hover:text-rose-700"
            onClick={(e) => {
              e.stopPropagation();
              onChange(null);
            }}
            data-testid={`${testid}-clear`}
          >
            <Trash2 className="w-4 h-4 mr-1" /> Remove
          </Button>
        )}
        {busy && (
          <div className="absolute inset-0 bg-white/70 grid place-items-center text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        )}
      </div>
    </div>
  );
}

export default function Onboarding() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [step, setStep] = useState(0);
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    org_name: "",
    industry: "",
    profile_picture: null,
    org_logo: null,
  });

  useEffect(() => {
    let mounted = true;
    api.get("/users/me/onboarding").then(({ data }) => {
      if (!mounted) return;
      setIndustries(data.industries || []);
      setForm({
        full_name: data.full_name || data.name || "",
        email: data.email || "",
        org_name: data.org_name || "",
        industry: data.industry || "",
        profile_picture: data.profile_picture || null,
        org_logo: data.org_logo || null,
      });
      // If the user already completed onboarding, kick them to the dashboard.
      if (data.onboarding_complete) {
        navigate("/", { replace: true });
      }
    }).catch(() => {
      navigate("/login", { replace: true });
    }).finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [navigate]);

  const persist = async (patch) => {
    try {
      const { data } = await api.patch("/users/me/onboarding", patch);
      return data;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't save");
      throw err;
    }
  };

  const stepValid = () => {
    if (step === 0) return form.full_name.trim().length > 1;
    if (step === 1) return form.org_name.trim().length > 1 && industries.includes(form.industry);
    return true; // images optional
  };

  const next = async () => {
    if (!stepValid()) {
      toast.error("Fill the required fields to continue");
      return;
    }
    setSaving(true);
    try {
      if (step === 0) {
        await persist({ full_name: form.full_name.trim() });
      } else if (step === 1) {
        await persist({ org_name: form.org_name.trim(), industry: form.industry });
      } else {
        const patch = {};
        if (form.profile_picture) patch.profile_picture = form.profile_picture;
        else patch.clear_profile_picture = true;
        if (form.org_logo) patch.org_logo = form.org_logo;
        else patch.clear_org_logo = true;
        await persist(patch);
        await api.post("/users/me/onboarding/complete");
        await refreshUser();
        toast.success("Welcome aboard! Your branding is live.");
        navigate("/", { replace: true });
        return;
      }
      setStep((s) => s + 1);
    } finally {
      setSaving(false);
    }
  };

  const back = () => setStep((s) => Math.max(0, s - 1));

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-900 text-slate-300">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-slate-50">
      <div className="max-w-3xl mx-auto px-6 py-12">
        {/* Brand row */}
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-3">
            <img src="/array-logo.png" alt="Array Engine" className="h-7 w-auto bg-white px-2 py-1 rounded-sm" />
            <div className="text-xs text-slate-400 tracking-widest uppercase">Onboarding</div>
          </div>
          <div className="text-xs text-slate-400">
            Step {step + 1} of {STEPS.length}
          </div>
        </div>

        {/* Stepper */}
        <div className="grid grid-cols-3 gap-3 mb-10" data-testid="onboarding-stepper">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const active = i === step;
            const done = i < step;
            return (
              <div
                key={s.key}
                className={`flex items-center gap-3 p-3 rounded-md border transition-colors ${
                  active ? "border-emerald-500 bg-emerald-500/10" :
                  done ? "border-emerald-500/40 bg-slate-800/50" :
                  "border-slate-700 bg-slate-800/30"
                }`}
              >
                <div className={`grid place-items-center w-8 h-8 rounded-sm ${active || done ? "bg-emerald-500 text-slate-900" : "bg-slate-700 text-slate-300"}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div>
                  <div className={`text-[10px] tracking-widest uppercase ${active || done ? "text-emerald-300" : "text-slate-500"}`}>Step {i + 1}</div>
                  <div className={`text-sm font-medium ${active || done ? "text-slate-50" : "text-slate-400"}`}>{s.title}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Step body */}
        <div className="bg-slate-50 text-slate-900 rounded-md p-8 shadow-xl">
          {step === 0 && (
            <div className="space-y-5" data-testid="onboarding-step-profile">
              <div>
                <h2 className="text-2xl font-semibold tracking-tight">Tell us about you</h2>
                <p className="text-sm text-slate-500 mt-1">
                  This is how your team will see you. You can change this later.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="ob-full-name">Full name<span className="text-rose-500"> *</span></Label>
                <Input
                  id="ob-full-name"
                  value={form.full_name}
                  onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
                  placeholder="Jane Doe"
                  data-testid="onboarding-full-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ob-email">Email</Label>
                <Input
                  id="ob-email"
                  value={form.email}
                  disabled
                  className="bg-slate-100"
                  data-testid="onboarding-email"
                />
                <p className="text-xs text-slate-400">Email is taken from your signup and can't be changed here.</p>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-5" data-testid="onboarding-step-org">
              <div>
                <h2 className="text-2xl font-semibold tracking-tight">Your organization</h2>
                <p className="text-sm text-slate-500 mt-1">
                  Used on every report you publish — donors, partners, and the public will see this.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="ob-org-name">Organization name<span className="text-rose-500"> *</span></Label>
                <Input
                  id="ob-org-name"
                  value={form.org_name}
                  onChange={(e) => setForm((f) => ({ ...f, org_name: e.target.value }))}
                  placeholder="Hope Foundation"
                  data-testid="onboarding-org-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Industry<span className="text-rose-500"> *</span></Label>
                <Select
                  value={form.industry}
                  onValueChange={(v) => setForm((f) => ({ ...f, industry: v }))}
                >
                  <SelectTrigger data-testid="onboarding-industry-trigger">
                    <SelectValue placeholder="Choose your sector" />
                  </SelectTrigger>
                  <SelectContent>
                    {industries.map((i) => (
                      <SelectItem key={i} value={i} data-testid={`onboarding-industry-${i.replace(/\s+/g, "-").toLowerCase()}`}>
                        {i}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6" data-testid="onboarding-step-branding">
              <div>
                <h2 className="text-2xl font-semibold tracking-tight">Add your branding</h2>
                <p className="text-sm text-slate-500 mt-1">
                  Your logo replaces Array Engine's branding on every web report and PDF you publish. Skip if you'd rather add later.
                </p>
              </div>
              <ImageDrop
                label="Profile picture"
                hint="Square image works best · up to 2 MB"
                value={form.profile_picture}
                onChange={(v) => setForm((f) => ({ ...f, profile_picture: v }))}
                testid="onboarding-profile-picture"
                rounded
              />
              <ImageDrop
                label="Organization logo"
                hint="Wide or square logo · used on reports & PDF · up to 2 MB"
                value={form.org_logo}
                onChange={(v) => setForm((f) => ({ ...f, org_logo: v }))}
                testid="onboarding-org-logo"
              />
              <div className="text-xs text-slate-500 flex items-center gap-2 bg-emerald-50 border border-emerald-200 px-3 py-2 rounded-sm">
                <Sparkles className="w-3.5 h-3.5 text-emerald-600" />
                Donors who open your share link will see this logo at the top of every page.
              </div>
            </div>
          )}

          <div className="mt-8 flex items-center justify-between">
            <Button
              type="button"
              variant="ghost"
              onClick={back}
              disabled={step === 0 || saving}
              data-testid="onboarding-back"
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <Button
              type="button"
              onClick={next}
              disabled={saving}
              className="bg-emerald-500 text-slate-900 hover:bg-emerald-400 font-semibold"
              data-testid="onboarding-next"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {step === STEPS.length - 1 ? "Finish" : "Continue"}
              {step !== STEPS.length - 1 && <ChevronRight className="w-4 h-4 ml-1" />}
            </Button>
          </div>
        </div>

        <div className="text-center mt-6 text-xs text-slate-500">
          Need help? Contact us — we'll get you set up in minutes.
        </div>
      </div>
    </div>
  );
}
